import { useEffect, useState } from 'react';
import { SupportTab } from './Support';
import { NavLink, useNavigate, useParams } from 'react-router';
import type { Masjid, SalahBlankKey, SalahBlankRule, SalahMode } from '@noorview/core';
import { MAX_MASJID_COLOURS, PIN_MAX, PIN_MIN, SALAH_DELAYS, SALAH_DURATIONS, SALAH_KEYS, SALAH_KEY_LABEL, delayLabel, hashExitPin, jumuahForEditing, normalizeMasjid, validPin, ymd } from '@noorview/core';
import { attempt, backend, useApp, useTheme, useUiStyle, type Theme, type UiStyle, demoMode } from '../data/store';
import { SandboxBackend } from '../data/sandbox';
import type { PhoneKey, Role } from '../data/backend';
import { Icon } from '../ui/icons';
import { Segmented, Switch, confirm } from '../ui/controls';
import { NumberField, SelectField, TextField } from '../ui/fields';
import { openSheet, useNav } from '../app/ui';
import { LockedArea } from '../app/lock';
import { leaveMasjid } from '../app/leave';
import { Select } from '../ui/pickers';
import { MasjidColoursEditor } from '../ui/MasjidColours';
import { canAddPerson, personLimitText } from '@noorview/core';

const TABS = [['masjid', 'Masjid'], ['jumuah', 'Jumu‘ah & Eid'], ['salah', 'Salah mode'], ['screenlock', 'Screen lock'], ['team', 'Team'], ['appearance', 'Appearance'], ['account', 'Account'], ['support', 'Support Centre']] as const;
export function SettingsPage() {
  const { tab: raw = 'masjid' } = useParams();
  // Prayer times now come only from the uploaded timetable; the old Prayer times tab opens Masjid.
  const tab = raw === 'prayer' ? 'masjid' : raw;
  return (
    <div className="page">
      <header className="page-head"><div><h1>Settings</h1><p>Changes here apply to every screen straight away.</p></div></header>
      <nav className="tabs" aria-label="Settings sections">
        {TABS.map(([k, l]) => <NavLink viewTransition key={k} to={`/settings/${k}`} className={({ isActive }) => (isActive || (tab === k) ? 'on' : '')}>{l}</NavLink>)}
      </nav>
      {tab === 'masjid' && <LockedArea what="the masjid’s details"><MasjidTab /></LockedArea>}
      {tab === 'jumuah' && <LockedArea what="Jumu‘ah and Eid times"><JumuahTab /></LockedArea>}
      {tab === 'screenlock' && <LockedArea what="the screen lock"><ScreenLockTab /></LockedArea>}
      {tab === 'team' && <TeamTab />}
      {tab === 'salah' && <LockedArea what="Salah mode"><SalahTab /></LockedArea>}
      {tab === 'appearance' && <AppearanceTab />}
      {tab === 'account' && <AccountTab />}
      {tab === 'support' && <SupportTab />}
    </div>
  );
}

/** Local copy of the masjid while editing, saved together. */
function useDraft() {
  const ws = useApp((s) => s.ws)!;
  const [m, setM] = useState<Masjid>(ws.masjid);
  useEffect(() => setM(ws.masjid), [ws.masjid]);
  const dirty = JSON.stringify(m) !== JSON.stringify(ws.masjid);
  // The logo and the exit PIN are saved on their own, so they're never overwritten here.
  const save = () => attempt(() => { const { logo: _logo, exitPinHash: _pin, ...rest } = normalizeMasjid(m); return backend.updateMasjid(rest); }, 'Saved');
  return { m, setM, dirty, save, reset: () => setM(ws.masjid) };
}

function SaveBar({ dirty, save, reset }: { dirty: boolean; save: () => unknown; reset: () => void }) {
  if (!dirty) return null;
  return <div className="savebar glass"><span>You have unsaved changes</span><span className="spacer" /><button type="button" className="btn sm plain" onClick={reset}>Discard</button><button type="button" className="btn sm primary" onClick={() => void save()}>Save</button></div>;
}

function MasjidTab() {
  const { m, setM, dirty, save, reset } = useDraft();
  const ws = useApp((s) => s.ws)!;
  const logo = useApp((s) => s.ws?.masjid.logo);
  const nav = useNav();
  const today = ymd(new Date(), m.timezone);
  const last = ws.timetable.at(-1)?.date;
  const covered = ws.timetable.filter((d) => d.date >= today).length;
  return (
    <section className="card glass settings-card">
      <div className="logo-row">
        <span className="logo-box ctl">{logo ? <img src={logo} alt="Masjid logo" /> : <Icon name="logo" size={28} />}</span>
        <div style={{ flex: 1, minWidth: 0 }}><b>Masjid logo</b><p className="hint" style={{ margin: '2px 0 0' }}>{logo ? 'Add it to any screen from the widget library (Masjid Logo).' : 'Upload your logo to use it on your screens.'}</p></div>
        <button type="button" className="btn sm plain" onClick={() => openSheet({ name: 'logo' })}><Icon name="upload" size={16} />{logo ? 'Change' : 'Upload logo'}</button>
      </div>
      <div className="sep" />
      <div className="mc-row">
        <div className="mc-head"><b>Masjid colours</b><span className="count hint">{(m.colors ?? []).length} of {MAX_MASJID_COLOURS}</span></div>
        <p className="hint" style={{ margin: 0 }}>Shown first in every colour choice in the Studio.</p>
        <MasjidColoursEditor value={m.colors ?? []} onChange={(colors) => setM({ ...m, colors })} logo={logo} />
      </div>
      <div className="sep" />
      <div className="two">
        <TextField label="Masjid name" value={m.name} max={80} onChange={(v) => setM({ ...m, name: v })} />
        <TextField label="Town or city" value={m.city ?? ''} max={60} onChange={(v) => setM({ ...m, city: v })} />
      </div>
      <div className="sep" />
      <div className="logo-row">
        <span className="logo-box ctl"><Icon name="sheet" size={26} /></span>
        <div style={{ flex: 1, minWidth: 0 }}><b>Prayer times</b>
          <p className="hint" style={{ margin: '2px 0 0' }}>{covered ? `Your timetable covers ${covered} day${covered === 1 ? '' : 's'} from today, up to ${last}. Every time on your screens comes from it.` : 'Upload your masjid’s timetable. Every time on your screens comes from it; nothing is calculated.'}</p></div>
        <button type="button" className="btn sm plain" onClick={() => nav('/timetable')}>View</button>
        <button type="button" className="btn sm primary" onClick={() => openSheet({ name: 'csv' })}><Icon name="upload" size={16} />Upload CSV</button>
      </div>
      <div className="row"><span className="label">Hijri date adjustment</span><span className="spacer" />
        <Segmented size="sm" label="Hijri adjustment" value={String(m.prayer.hijriAdjust)} onChange={(v) => setM({ ...m, prayer: { ...m.prayer, hijriAdjust: Number(v) } })} options={['-2', '-1', '0', '1', '2'].map((v) => ({ value: v, label: v === '0' ? '0' : `${+v > 0 ? '+' : ''}${v}` }))} /></div>
      <p className="hint">The Hijri date is worked out automatically. Use this to match your local moon sighting.</p>
      <SaveBar dirty={dirty} save={save} reset={reset} />
    </section>
  );
}

const SALAH_HINT: Partial<Record<SalahBlankKey, string>> = {
  maghrib: 'The adhan is usually at the jama‘ah time, so wait a few minutes.',
  jumuah: 'Counted from each Jumu‘ah jama‘ah time. Replaces Dhuhr on Fridays.',
  eid: 'Counted from each Eid salah time you’ve added.',
};

function SalahTab() {
  const { m, setM, dirty, save, reset } = useDraft();
  const ws = useApp((s) => s.ws)!;
  const sm = m.prayer.salahMode;
  const set = (patch: Partial<SalahMode>) => setM({ ...m, prayer: { ...m.prayer, salahMode: { ...sm, ...patch } } });
  const setRule = (k: SalahBlankKey, patch: Partial<SalahBlankRule>) => set({ prayers: { ...sm.prayers, [k]: { ...sm.prayers[k], ...patch } } });
  return (
    <section className="card glass settings-card">
      <div className="toggle-row">
        <div><b className="t17">Blank screens during salah</b>
          <p className="hint" style={{ margin: '4px 0 0', maxWidth: 560 }}>Shortly after each jama‘ah starts, screens fade to black and show only a small clock, so nobody is distracted while praying. They come back on by themselves. Screens do this on their own, even without internet.</p></div>
        <Switch label="Blank screens during salah" checked={sm.enabled} onChange={(v) => set({ enabled: v })} />
      </div>
      <div className={`salah-body${sm.enabled ? '' : ' off'}`}>
        <div className="field"><span>Clock while blank</span>
          <div className="salah-pos" role="radiogroup" aria-label="Clock position">
            {(['top-left', 'top-center', 'top-right'] as const).map((pos) => (
              <button key={pos} type="button" role="radio" aria-checked={sm.clock === pos} className={`salah-pos-opt${sm.clock === pos ? ' on' : ''}`} onClick={() => set({ clock: pos })}>
                <span className={`salah-mini p-${pos}`}><i>{sm.h12 ? '1:32' : '13:32'}</i></span>
                <small>{pos === 'top-left' ? 'Top left' : pos === 'top-center' ? 'Top centre' : 'Top right'}</small>
              </button>
            ))}
          </div>
        </div>
        <div className="field"><span>Clock format</span>
          <Segmented className="fit-seg" label="Clock format" value={sm.h12 ? '12' : '24'} onChange={(v) => set({ h12: v === '12' })} options={[{ value: '24', label: '24-hour' }, { value: '12', label: '12-hour' }]} />
        </div>
        <div className="field"><span>Timing for each salah</span>
          <div className="salah-rules">
            <div className="sr-head"><span>Salah</span><span>Goes blank after jama‘ah</span><span>Stays blank for</span></div>
            {SALAH_KEYS.map((k) => {
              const r = sm.prayers[k];
              return (
                <div key={k} className={`sr-row ctl${r.on ? '' : ' off'}`}>
                  <span className="sr-name"><Switch label={`Blank for ${SALAH_KEY_LABEL[k]}`} checked={r.on} onChange={(v) => setRule(k, { on: v })} /><b>{SALAH_KEY_LABEL[k]}</b></span>
                  <Select label={`${SALAH_KEY_LABEL[k]}: goes blank after`} disabled={!r.on} value={String(r.delaySec)} onChange={(v) => setRule(k, { delaySec: Number(v) })}
                    options={SALAH_DELAYS.map((d) => ({ value: String(d), label: delayLabel(d) }))} />
                  <Select label={`${SALAH_KEY_LABEL[k]}: stays blank for`} disabled={!r.on} value={String(r.minutes)} onChange={(v) => setRule(k, { minutes: Number(v) })}
                    options={(SALAH_DURATIONS as readonly number[]).concat(SALAH_DURATIONS.includes(r.minutes as never) ? [] : [r.minutes]).sort((a, b) => a - b).map((d) => ({ value: String(d), label: `${d} minutes` }))} />
                  {SALAH_HINT[k] && <small className="hint sr-hint">{SALAH_HINT[k]}</small>}
                </div>
              );
            })}
          </div>
          <small className="hint">Jama‘ah times come from your timetable and jama‘ah rules. A salah without a jama‘ah time is skipped.</small>
        </div>
        <div className="field"><span>Screens</span>
          <div className="team">
            {ws.screens.map((s) => (
              <li key={s.id} className="ctl toggle-row" style={{ listStyle: 'none' }}>
                <span><b>{s.name}</b><small className="hint" style={{ display: 'block' }}>{s.salahBlank === false ? 'Stays on during salah' : 'Blanks during salah'}{s.locked ? ' · Locked, unlock the screen to change this' : ''}</small></span>
                <Switch label={`${s.name} blanks during salah`} checked={s.salahBlank !== false} disabled={s.locked} onChange={(v) => void attempt(() => backend.updateScreen(s.id, { salahBlank: v }))} />
              </li>
            ))}
          </div>
          <small className="hint">Turn a screen off here to keep it showing as normal, for example one in the entrance or the car park.</small>
        </div>
      </div>
      <SaveBar dirty={dirty} save={save} reset={reset} />
    </section>
  );
}

/**
 * Screen lock: paired displays can't be left from the screen itself. The exit PIN lets a person at the screen out for
 * two minutes; without one, only unpairing releases a display.
 */
function ScreenLockTab() {
  const ws = useApp((s) => s.ws)!;
  const admin = useApp((s) => s.session?.role !== 'editor');
  const [pin, setPin] = useState('');
  const [again, setAgain] = useState('');
  const has = !!ws.masjid.exitPinHash;
  const digits = (v: string) => v.replace(/\D/g, '').slice(0, PIN_MAX);
  const problem = pin && !validPin(pin) ? `Use ${PIN_MIN} to ${PIN_MAX} digits.` : again && again !== pin ? 'The two PINs don’t match.' : '';
  const save = async () => {
    if (!validPin(pin) || pin !== again) return;
    if (await attempt(() => backend.updateMasjid({ exitPinHash: hashExitPin(ws.masjid.id, pin) }), has ? 'Exit PIN changed for every screen' : 'Exit PIN set for every screen')) { setPin(''); setAgain(''); }
  };
  const remove = async () => {
    if (await confirm('Remove the exit PIN?', { body: 'Locked screens can then only be released by unpairing them here or in the iPhone app.', ok: 'Remove PIN', danger: true })) await attempt(() => backend.updateMasjid({ exitPinHash: null }), 'Exit PIN removed');
  };
  return (
    <section className="card glass settings-card">
      <div><b className="t17">Locked screens</b>
        <p className="hint" style={{ margin: '4px 0 0', maxWidth: 620 }}>Once a screen is paired, nobody at the screen can leave NoorView: touches, clicks, the keyboard and the remote do nothing. To release a screen, unpair it from Screens, here or in the iPhone app.</p></div>
      <div className="row"><span className="label">Exit PIN</span><span className="spacer" /><span className={`pill ${has ? 'online' : 'off'}`}>{has ? 'Set' : 'Not set'}</span></div>
      <p className="hint" style={{ margin: 0 }}>With an exit PIN, someone at the screen can leave for two minutes: touching the screen or pressing a key on its keyboard or remote asks for the PIN. Five wrong tries and the screen waits a minute. Without a PIN, touches and keys do nothing.</p>
      {admin ? (<>
        <div className="two">
          <label className="field"><span>{has ? 'New PIN' : 'PIN'}</span><input className="input mono" type="password" inputMode="numeric" autoComplete="new-password" value={pin} onChange={(e) => setPin(digits(e.target.value))} placeholder={`${PIN_MIN}–${PIN_MAX} digits`} /></label>
          <label className="field"><span>Type it again</span><input className="input mono" type="password" inputMode="numeric" autoComplete="new-password" value={again} onChange={(e) => setAgain(digits(e.target.value))} /></label>
        </div>
        {problem && <div className="banner bad">{problem}</div>}
        <div className="row">
          <button type="button" className="btn primary" disabled={!validPin(pin) || pin !== again} onClick={() => void save()}>{has ? 'Change PIN' : 'Set PIN'}</button>
          {has && <button type="button" className="btn plain danger-text" onClick={() => void remove()}>Remove PIN</button>}
        </div>
        <p className="hint" style={{ margin: 0 }}>Only a fingerprint of the PIN is saved, never the PIN itself. Screens check it on their own, so it works without internet.</p>
      </>) : <div className="banner info">Only an admin can set or change the exit PIN.</div>}
    </section>
  );
}

function JumuahTab() {
  const ws = useApp((s) => s.ws)!;
  return (
    <section className="card glass settings-card">
      <div className="card-head"><h2>Jumu‘ah</h2><span className="spacer" /><button type="button" className="btn sm primary" onClick={() => openSheet({ name: 'jumuah' })}>Edit</button></div>
      {(() => { const jm = jumuahForEditing(ws.masjid, ws.timetable, ymd(new Date(), ws.masjid.timezone)).slots; return jm.length ? jm.map((j, i) => <div key={i} className="row"><Icon name="calendar" size={18} /><b>{j.label}</b><span className="spacer" /><span className="mono">Khutbah {j.khutbah} · Jama‘ah {j.jamaah}</span></div>) : <p className="hint">No Jumu‘ah times set.</p>; })()}
      <div className="sep" />
      <div className="card-head"><h2>Eid</h2><span className="spacer" /><button type="button" className="btn sm primary" onClick={() => openSheet({ name: 'eid' })}>Edit</button></div>
      {ws.masjid.prayer.eid.length ? ws.masjid.prayer.eid.map((e) => <div key={e.id} className="row"><Icon name="moon" size={18} /><b>{e.name}</b><span className="hint">{new Date(`${e.date}T12:00:00Z`).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' })}</span><span className="spacer" /><span className="mono">{e.takbeer ? `Takbeer ${e.takbeer} · ` : ''}Salah {e.jamaats.join(', ')}</span></div>) : <p className="hint">No Eid salah times set.</p>}
    </section>
  );
}

function TeamTab() {
  const ws = useApp((s) => s.ws)!;
  const session = useApp((s) => s.session);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('editor');
  const canManage = session?.role === 'owner' || session?.role === 'admin';
  const nav = useNavigate();
  const leave = async () => { if (await leaveMasjid()) nav('/login', { replace: true }); };
  return (
    <section className="card glass settings-card">
      <div className="card-head"><h2>Team</h2><span className="count">{ws.members.length} {ws.members.length === 1 ? 'person' : 'people'}</span></div>
      <ul className="team">
        {ws.members.map((m) => (
          <li key={m.id} className="row ctl">
            <span className="avatar" style={{ width: 36, height: 36, fontSize: 13 }}>{m.name.slice(0, 2).toUpperCase()}</span>
            <span style={{ flex: 1, minWidth: 0 }}><b>{m.name}</b><small className="hint" style={{ display: 'block' }}>{m.email}</small></span>
            {canManage && m.role !== 'owner'
              ? <Select label={`${m.name}’s role`} style={{ width: 120 }} value={m.role} onChange={(v) => void attempt(() => backend.updateMemberRole(m.id, v as Role), `${m.name} is now ${v === 'admin' ? 'an admin' : 'an editor'}`)} options={[{ value: 'editor', label: 'Editor' }, { value: 'admin', label: 'Admin' }]} />
              : <span className="pill off">{m.role === 'owner' ? 'Owner' : m.role === 'admin' ? 'Admin' : 'Editor'}</span>}
            {m.id === session?.userId
              ? <button type="button" className="btn sm plain danger-text" onClick={() => void leave()} title={ws.members.length === 1 ? 'You’re the last person here: leaving closes the masjid’s NoorView account' : 'Leave this masjid’s team'}><Icon name="logout" size={16} />Leave</button>
              : canManage && m.role !== 'owner' && <button type="button" className="lbtn danger" aria-label={`Remove ${m.name}`} onClick={async () => { if (await confirm(`Remove ${m.name}?`, { ok: 'Remove', danger: true })) await attempt(() => backend.removeMember(m.id), 'Removed'); }}><Icon name="trash" size={16} /></button>}
          </li>
        ))}
      </ul>
      {canManage && !canAddPerson(ws.masjid.plan, ws.members.length) && (
        <div className="row ctl" style={{ gap: 12, alignItems: 'center' }}>
          <span style={{ flex: 1 }} className="hint">{personLimitText(ws.masjid.plan)}</span>
          <button type="button" className="btn primary" onClick={() => openSheet({ name: 'upgrade', reason: 'users' })}>See plans</button>
        </div>
      )}
      {canManage && canAddPerson(ws.masjid.plan, ws.members.length) && (
        <form className="row invite" onSubmit={async (e) => { e.preventDefault(); const ok = await attempt(() => backend.inviteMember(email, role), `Invitation sent to ${email}`); if (ok !== undefined) setEmail(''); }}>
          <input className="input" type="email" placeholder="name@masjid.org" value={email} onChange={(e) => setEmail(e.target.value)} aria-label="Email to invite" required />
          <Select label="Role" style={{ width: 140 }} value={role} onChange={(v) => setRole(v)} options={[{ value: 'editor', label: 'Editor' }, { value: 'admin', label: 'Admin' }]} />
          <button className="btn primary">Invite</button>
        </form>
      )}
      <p className="hint">Editors can design and send screens live. Admins can also change settings and the team.</p>
    </section>
  );
}

function AppearanceTab() {
  const theme = useTheme();
  const ui = useUiStyle();
  const styles: Array<{ value: UiStyle; label: string; note: string }> = [
    { value: 'glass', label: 'Glass', note: 'Frosted panels with depth' }, { value: 'simple', label: 'Simple', note: 'Flat and minimal, no shadows' },
  ];
  const opts: Array<{ value: Theme; label: string; note: string }> = [
    { value: 'legacy', label: 'Legacy', note: 'NoorView’s deep blue' }, { value: 'light', label: 'Light', note: 'Bright frosted glass' }, { value: 'dark', label: 'Dark', note: 'Black with yellow accents' },
  ];
  return (
    <section className="card glass settings-card">
      <div><b className="t17">Appearance</b><p className="hint" style={{ margin: '4px 0 0' }}>How the dashboard and Studio look on this computer. Your screens aren’t affected.</p></div>
      <div className="theme-cards" role="radiogroup" aria-label="Interface style">
        {styles.map((o) => (
          <button key={o.value} type="button" role="radio" aria-checked={ui.ui === o.value} className={`theme-card ctl${ui.ui === o.value ? ' on' : ''}`} onClick={() => ui.set(o.value)}>
            <span className={`ui-swatch u-${o.value}`} aria-hidden><i /><i /></span>
            <b>{o.label}</b><small>{o.note}</small>
          </button>
        ))}
      </div>
      <div className="theme-cards" role="radiogroup" aria-label="Appearance">
        {opts.map((o) => (
          <button key={o.value} type="button" role="radio" aria-checked={theme.theme === o.value} className={`theme-card ctl${theme.theme === o.value ? ' on' : ''}`} onClick={() => theme.set(o.value)}>
            <span className={`theme-swatch t-${o.value}`} aria-hidden><i /><i /><i /></span>
            <b>{o.label}</b><small>{o.note}</small>
          </button>
        ))}
      </div>
    </section>
  );
}

function AccountTab() {
  const session = useApp((s) => s.session);
  const sandbox = backend instanceof SandboxBackend ? backend : null;
  const nav = useNav();
  return (
    <section className="card glass settings-card">
      <div className="row"><span className="avatar">{(session?.name ?? '?').slice(0, 2).toUpperCase()}</span><span><b>{session?.name}</b><small className="hint" style={{ display: 'block' }}>{session?.email}</small></span></div>
      <div className="row">
        <button type="button" className="btn plain" onClick={async () => { await backend.signOut(); useApp.getState().setSession(null); nav('/login'); }}><Icon name="logout" size={18} />Sign out</button>
        {sandbox && <button type="button" className="btn plain danger-text" onClick={async () => { if (await confirm(demoMode ? 'Reset the demo?' : 'Reset the sandbox?', { body: demoMode ? 'Everything in this demo goes back to the starting example. Open virtual screens will need pairing again.' : 'Everything in this browser’s sandbox goes back to the starting example. Open virtual screens will need pairing again.', ok: 'Reset', danger: true })) { await sandbox.reset(); await useApp.getState().reload(); } }}><Icon name="refresh" size={18} />{demoMode ? 'Reset demo' : 'Reset sandbox'}</button>}
      </div>
      {backend.changePassword && <PasswordCard />}
      {backend.phoneKeys && <PhonesCard />}
    </section>
  );
}

/** Choose a new password for this account. */
function PasswordCard() {
  const [pw, setPw] = useState('');
  const [again, setAgain] = useState('');
  const problem = pw && pw.length < 10 ? 'Use at least 10 characters.' : again && again !== pw ? 'The two passwords don’t match.' : '';
  const save = async () => { if (await attempt(() => backend.changePassword!(pw), 'Password changed')) { setPw(''); setAgain(''); } };
  return (<>
    <div className="sep" />
    <div><b className="t17">Change password</b><p className="hint" style={{ margin: '4px 0 0' }}>You’ll use it with your email to sign in.</p></div>
    <div className="two">
      <label className="field"><span>New password</span><input className="input" type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} /></label>
      <label className="field"><span>Type it again</span><input className="input" type="password" autoComplete="new-password" value={again} onChange={(e) => setAgain(e.target.value)} /></label>
    </div>
    {problem && <div className="banner bad">{problem}</div>}
    <div className="row"><button type="button" className="btn primary" disabled={pw.length < 10 || pw !== again} onClick={() => void save()}>Change password</button></div>
  </>);
}

/** Phones that can use NoorView's widgets, Control Centre buttons and Siri. Removing one turns those off at once. */
function PhonesCard() {
  const ws = useApp((s) => s.ws)!;
  const session = useApp((s) => s.session);
  const admin = session?.role !== 'editor';
  const [phones, setPhones] = useState<PhoneKey[] | null>(null);
  const load = () => backend.phoneKeys?.().then(setPhones).catch(() => setPhones([]));
  useEffect(() => { void load(); }, []);
  const owner = (id: string) => (id === session?.userId ? 'You' : ws.members.find((m) => m.id === id)?.name ?? 'Someone who left');
  const used = (p: PhoneKey) => (p.lastUsedAt ? `Last used ${new Date(p.lastUsedAt).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}` : 'Not used yet');
  return (<>
    <div className="sep" />
    <div><b className="t17">Phone controls</b>
      <p className="hint" style={{ margin: '4px 0 0', maxWidth: 620 }}>Phones signed in to the NoorView iPhone app can show your screens in widgets and turn them on, off or idle, or lock them, from the Home Screen, the Lock Screen, Control Centre and Siri. The phone has to be unlocked first. {admin ? 'You can see everyone’s phones.' : 'These are your phones.'}</p></div>
    {phones === null ? <p className="hint">Loading…</p> : phones.length === 0 ? <p className="hint">No phones yet. Sign in to the iPhone app to add one.</p> : (
      <ul className="team">
        {phones.map((p) => (
          <li key={p.id} className="row ctl">
            <Icon name="phone" size={20} />
            <span style={{ flex: 1, minWidth: 0 }}><b>{p.phoneName}</b><small className="hint" style={{ display: 'block' }}>{owner(p.userId)} · {used(p)}</small></span>
            <button type="button" className="lbtn danger" aria-label={`Remove ${p.phoneName}`} onClick={async () => { if (await confirm(`Remove ${p.phoneName}?`, { body: 'Its widgets, Control Centre buttons and Siri stop working straight away. Signing in to the app on it again adds it back.', ok: 'Remove', danger: true })) { await attempt(() => backend.removePhoneKey!(p.id), 'Phone removed'); void load(); } }}><Icon name="trash" size={16} /></button>
          </li>
        ))}
      </ul>
    )}
  </>);
}
